import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Card from "@churchofjesuschrist/eden-card";
import { Clear } from "@churchofjesuschrist/eden-icons";
import { info10, grey5, spacing4, spacing8, spacing16, spacing24, white } from "@churchofjesuschrist/eden-style-constants";
import { Icon as EdenButton } from "@churchofjesuschrist/eden-buttons";
const barWidth = spacing8;
const borderRadius = "2px";
const border = `1px solid ${grey5}`;
const halfSpace = spacing4;
const space = spacing8;
const AlertCard = styled(Card).withConfig({
  componentId: "sc-pay24v-0"
})(["display:flex;border:0;"]);
/*
In order to achieve the overall 2px border radius AND the thicker, colored
border on the inline-start side, where the color goes all the way to the top
and bottom AND have a 90-degree angle on the inside, we need some layout
trickery. CSS miters corners so by default we end up with a weird border angle
on the inside.

We use a `Layout` with a `Bar` at the start for the thicker, colored border and
an `End` at the other end. Each of these handle the border radius for their
side. `ContentWrapper` in the middle covers up the "inner" border radii of `Bar`
and `End` in order to complete the effect of having an internal 90-degree angle
next to the `Bar`.

In the future this could probably be simplified with the use of CSS Logical
Properties for border-radius.
*/

const Layout = styled.div.withConfig({
  componentId: "sc-pay24v-1"
})(["position:relative;z-index:0;width:100%;display:flex;"]);
const Bar = styled.div.withConfig({
  componentId: "sc-pay24v-2"
})(["width:calc(", " + ", ");background-color:", ";border-radius:", ";"], barWidth, borderRadius, _ref => {
  let {
    borderColor
  } = _ref;
  return borderColor;
}, borderRadius);
const ContentWrapper = styled.div.withConfig({
  componentId: "sc-pay24v-3"
})(["background-color:", ";margin:0 -", ";width:100%;border-block-start:", ";border-block-end:", ";z-index:1;display:flex;align-items:flex-start;padding:", " ", ";padding-inline-end:", ";"], white, borderRadius, border, border, spacing16, halfSpace, space);
const End = styled.div.withConfig({
  componentId: "sc-pay24v-4"
})(["width:calc(", " * 2);border:", ";border-radius:", ";background-color:", ";"], borderRadius, border, borderRadius, white);
const IconWrapper = styled.div.withConfig({
  componentId: "sc-pay24v-5"
})(["margin:0 ", ";display:flex;"], halfSpace);
const Content = styled.div.withConfig({
  componentId: "sc-pay24v-6"
})(["flex:1;margin:0 ", ";"], halfSpace);
const Close = styled.div.withConfig({
  componentId: "sc-pay24v-7"
})(["margin:0 ", ";display:flex;"], halfSpace);
const Button = styled(EdenButton).withConfig({
  componentId: "sc-pay24v-8"
})(["margin:-", " 0;"], halfSpace);

const Alert = _ref2 => {
  let {
    children,
    onClose,
    Icon,
    color = info10,
    ...props
  } = _ref2;
  return /*#__PURE__*/React.createElement(AlertCard, props, /*#__PURE__*/React.createElement(Layout, null, /*#__PURE__*/React.createElement(Bar, {
    borderColor: color
  }), /*#__PURE__*/React.createElement(ContentWrapper, null, /*#__PURE__*/React.createElement(IconWrapper, null, Icon && /*#__PURE__*/React.createElement(Icon, {
    size: spacing24,
    color: color
  })), /*#__PURE__*/React.createElement(Content, null, children), /*#__PURE__*/React.createElement(Close, null, onClose && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: onClose
  }, /*#__PURE__*/React.createElement(Clear, {
    size: spacing24
  })))), /*#__PURE__*/React.createElement(End, null)));
};

Alert.propTypes = {
  /** Content inside the alert */
  children: PropTypes.node.isRequired,

  /** Callback function to execute when user dismisses the alert */
  onClose: PropTypes.func,

  /** The main color value to apply. */
  color: PropTypes.string,

  /** The icon component to use. */
  Icon: PropTypes.func
};
export default Alert;